import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Confetti from '../components/Confetti';
import { 
  Trophy,
  Fire,
  Timer,
  House,
  PencilSimple,
  Share
} from '@phosphor-icons/react';

const SessionComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const log = location.state?.log;

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes} min`;
  };

  return (
    <div 
      className="min-h-screen bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden hexagon-bg"
      data-testid="session-complete-screen"
    >
      <Confetti />
      
      {/* Gold glow */}
      <div className="absolute inset-0 bg-gradient-radial from-[#D4AF37]/10 via-transparent to-transparent" />

      <div className="max-w-md w-full text-center relative z-10 animate-fade-in-up">
        {/* Trophy */}
        <div className="mb-8 relative inline-block">
          <div className="absolute -inset-8 bg-[#D4AF37]/20 blur-3xl rounded-full" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-3xl flex items-center justify-center shadow-2xl">
            <Trophy weight="fill" className="w-12 h-12 text-black" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Great Work!
        </h1>
        <p className="text-[#D4AF37] text-lg font-semibold mb-8">Session Complete</p>

        {/* Session Summary */}
        {log && (
          <div className="premium-card p-6 mb-8 text-left">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Session Summary</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Cardio Type</span>
                <span className="text-foreground font-semibold capitalize">{log.cardio_type?.replace('_', ' ')}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Timer weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-muted-foreground">Duration</span>
                </div>
                <span className="text-foreground font-semibold">{formatDuration(log.duration_minutes)}</span>
              </div>
              
              {log.calories_burned && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Fire weight="fill" className="w-5 h-5 text-orange-500" />
                    <span className="text-muted-foreground">Calories</span>
                  </div>
                  <span className="text-foreground font-semibold">{log.calories_burned} cal</span>
                </div>
              )}
              
              {log.distance && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Distance</span>
                  <span className="text-foreground font-semibold">{log.distance} mi</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Motivational message */}
        <p className="text-muted-foreground mb-8">
          Every session brings you closer to your 365-day goal. Keep the momentum going!
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover"
            data-testid="home-btn"
          >
            <House weight="fill" className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/log')}
              variant="outline"
              className="flex-1 border-[#D4AF37]/30 text-foreground rounded-full py-5 hover:bg-[#D4AF37]/5"
              data-testid="add-details-btn"
            >
              <PencilSimple weight="duotone" className="w-5 h-5 mr-2" />
              Add Details
            </Button>
            
            <Button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Track365 Cardio',
                    text: `Just completed a ${log?.duration_minutes || 0} minute ${log?.cardio_type?.replace('_', ' ') || 'cardio'} session! 🔥`,
                  });
                }
              }}
              variant="outline"
              className="flex-1 border-border text-foreground rounded-full py-5 hover:bg-card"
              data-testid="share-btn"
            >
              <Share weight="duotone" className="w-5 h-5 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionComplete;
